/**
 * Refund v2 — request + response contract for
 * `POST /api/orders/refund` and `GET /api/orders/seller-orders/[id]/refund-state`.
 *
 * Phase 3 of the REFUND_V2 rollout. Phase 1 (DB — `refund_attempts` ledger +
 * cumulative-total trigger + FSM-freeze trigger) shipped on 2026-07-09 with
 * migration `20260709084555_refund_v2_refund_attempts.sql`. This file is the
 * public wire boundary the modal / mobile client parse against; the domain
 * layer (`lib/payments/refund.ts`) and thin route (`app/api/orders/refund/`)
 * follow in Phase 3.
 *
 * Scope decisions (§10 Q1/Q8, locked 2026-07-09):
 *  - Q1 = G1 (no fee refund); G3 stays unlockable behind a domain flag — the
 *    ledger already carries per-attempt `service_fee_refund` / `platform_fee_refund`
 *    columns from Phase 1. This endpoint's REQUEST does NOT expose a
 *    `includeFees` toggle yet; policy is domain-owned.
 *  - Q8 = shipping component is NOT refunded once shipped. `shippingAmount > 0`
 *    is rejected once label is purchased+scanned (states B-scanned/C/D) with
 *    `SHIPPING_NOT_REFUNDABLE_AFTER_SHIP`; the refund-state GET surfaces
 *    `shippingRefundable: boolean` so the modal disables the toggle before
 *    submit.
 *  - Q8-b (added 2026-07-10) — **shippo/live-rate goodwill: shipping NEVER
 *    refundable.** Independent of label state: even in state A (label not
 *    bought) the platform cannot guarantee P&L=0 on the goodwill entry
 *    point, because `POST /orders/refund` and the label-print button don't
 *    cross-check — a shipping-goodwill followed by a label-print leaves
 *    Kraahl out-of-pocket for the label cost. Wire code:
 *    `SHIPPING_NOT_REFUNDABLE_LIVE_RATE`. Cancel-path (`cancel-item` /
 *    `update-status`) is unaffected — it still refunds shipping in state A
 *    because it also voids the label in the same transaction.
 *
 * The request intentionally omits `source`: this endpoint ONLY issues goodwill
 * refunds. Cancel-path lives on `POST /api/orders/cancel-item` / `update-status`,
 * and dispute-path is Phase 3.5. `refund_attempts.source` in the ledger is the
 * union of all three, but at the wire the endpoint is single-source.
 *
 * @see .tondocs/payments/REFUND_V2.md §5.1 — request/response
 * @see .tondocs/payments/REFUND_V2.md §5.3 — GET refund-state
 * @see .tondocs/payments/REFUND_V2.md §7 — edge cases (dispute, sequential partials, currency)
 * @see .tondocs/payments/REFUND_V2.md §10 — Q1 (G1 fee policy) + Q8 (shipping-post-ship)
 * @see .tondocs/skills/api-contract-design.md §A1 — machine code + human message
 * @see .tondocs/skills/type-driven-design.md §1 §3 — ADT + exhaustiveness
 * @see .tondocs/WEB_MOBILE_CONTRACT.md §8 G — interface is SoT via `pinned<T>()`
 */

import { z } from "zod";
import { pinned } from "./pin";

// ---------------------------------------------------------------------------
// Request — `POST /api/orders/refund`
// ---------------------------------------------------------------------------

/**
 * Per-item entry in a refund request. `amount` is the buyer-facing refund
 * attributed to this item line (excluding shipping + service fee). Must be
 * positive; must not exceed the item's remaining refundable budget (enforced
 * server-side by the domain layer + the DB cumulative-total trigger). See
 * REFUND_V2.md §3.1 (`refund_attempts.items` shape) and §7.1 (sequential
 * partials on the same item).
 */
export interface RefundRequestItem {
  orderItemId: string;
  amount: number;
}

/**
 * `POST /api/orders/refund` body. Idempotency key travels on the
 * `Idempotency-Key` header (per REFUND_V2.md §3.5 — client-generated UUIDv4
 * at modal-open, re-used on retry). Endpoint issues goodwill refunds only;
 * no `source` field is exposed on the wire.
 */
export interface RefundRequest {
  sellerOrderId: string;
  items: RefundRequestItem[];
  /** Independent shipping refund. Defaults to 0. Server rejects >0 on
   *  shippo/live-rate orders unconditionally (`SHIPPING_NOT_REFUNDABLE_LIVE_RATE`,
   *  Q8-b) and on flat-rate orders once the label is purchased+scanned
   *  (`SHIPPING_NOT_REFUNDABLE_AFTER_SHIP`, Q8). */
  shippingAmount?: number;
  /** Seller-facing note. ≤500 chars. */
  reason?: string;
  /** Buyer-facing note, forwarded to PayPal `noteToPayer`. ≤200 chars. */
  noteToPayer?: string;
}

const RefundRequestItemSchema = z.object({
  orderItemId: z.string().uuid(),
  amount: z.number().positive(),
});

export const RefundRequestSchema = pinned<RefundRequest>()(
  z.object({
    sellerOrderId: z.string().uuid(),
    items: z.array(RefundRequestItemSchema).min(1),
    shippingAmount: z.number().min(0).optional(),
    reason: z.string().max(500).optional(),
    noteToPayer: z.string().max(200).optional(),
  }),
);

// ---------------------------------------------------------------------------
// Response — discriminated union on `ok`
// ---------------------------------------------------------------------------

/**
 * `refund.status` values reachable at wire-level. `pending` = attempt row
 * inserted + PayPal call issued, awaiting webhook confirmation.
 * `completed` = provider acknowledged the refund synchronously.
 * `failed` / `reversed` / `cancelled` are ledger-only terminal states and
 * NEVER appear on this endpoint's success response (a failed provider call
 * surfaces as `{ ok: false, code: "PROVIDER_ERROR" }` instead — the ledger
 * still records the row; UI polls refund-state GET for lifecycle updates).
 */
export const REFUND_RESPONSE_KINDS = ["pending", "completed"] as const;
export type RefundResponseKind = (typeof REFUND_RESPONSE_KINDS)[number];

export interface RefundSuccessResponse {
  ok: true;
  refund: {
    id: string;
    status: RefundResponseKind;
    providerRefundId?: string;
    totalAmount: number;
    currency: string;
  };
}

export interface RefundErrorResponse {
  ok: false;
  code: RefundErrorCode;
  message: string;
  details?: unknown;
}

export type RefundResponse = RefundSuccessResponse | RefundErrorResponse;

// ---------------------------------------------------------------------------
// Error codes — legacy `{ error, code }` peers of the payments registry.
// ---------------------------------------------------------------------------

/**
 * Stable wire-level error codes for `POST /api/orders/refund`. UI / mobile
 * pattern-match on `code`, never on `message`. Once mobile pins these, values
 * FREEZE — never rename, never recycle.
 *
 * HTTP status mapping (owned by the route in Phase 3):
 *  - 403 NOT_AUTHORIZED                    — caller is not the seller
 *  - 404 SELLER_ORDER_NOT_FOUND            — seller_order id does not exist
 *  - 200 ALREADY_REFUNDED                  — same idempotency-key row exists;
 *                                            body carries the existing row
 *                                            (idempotency-correct, per §5.1
 *                                            "actually returns 200 with the
 *                                            existing row")
 *  - 422 EXCEEDS_REFUNDABLE                — cumulative would exceed captured
 *                                            (trigger 23514 → this code)
 *  - 409 DISPUTE_OPEN                      — PayPal dispute locks the capture
 *  - 502 PROVIDER_ERROR                    — PayPal returned non-2xx
 *  - 422 PROVIDER_LEGACY_NO_CAPTURE        — pre-unified-fee order, no
 *                                            capture_id (manual refund path)
 *  - 423 LOCKED                            — `seller_orders.cancel_in_progress`
 *                                            CAS held
 *  - 422 SHIPPING_NOT_REFUNDABLE_AFTER_SHIP — Q8: flat-rate shipping component
 *                                            locked once label is
 *                                            purchased+scanned
 *  - 422 SHIPPING_NOT_REFUNDABLE_LIVE_RATE  — Q8-b: shippo/live-rate goodwill
 *                                            NEVER refunds shipping (no
 *                                            cross-check with label-print
 *                                            → out-of-pocket risk)
 *  - 400 INVALID_REQUEST                   — Zod safeParse failure at the
 *                                            boundary
 *  - 422 CURRENCY_MISMATCH                 — `items[]` or an explicit currency
 *                                            hint disagrees with the
 *                                            `seller_orders.currency`
 *
 * @see .tondocs/payments/REFUND_V2.md §5.1
 */
export const REFUND_ERROR_CODES = [
  "NOT_AUTHORIZED",
  "SELLER_ORDER_NOT_FOUND",
  "ALREADY_REFUNDED",
  "EXCEEDS_REFUNDABLE",
  "DISPUTE_OPEN",
  "PROVIDER_ERROR",
  "PROVIDER_LEGACY_NO_CAPTURE",
  "LOCKED",
  "SHIPPING_NOT_REFUNDABLE_AFTER_SHIP",
  "SHIPPING_NOT_REFUNDABLE_LIVE_RATE",
  "INVALID_REQUEST",
  "CURRENCY_MISMATCH",
] as const;

export type RefundErrorCode = (typeof REFUND_ERROR_CODES)[number];

// Compile-time exhaustiveness pin — array↔union. Mirrors the ready-to-ship
// precedent (L109-121). Same intent: extending one side without the other
// fails tsc at this export site with expected/actual types in the error.
type _AssertRefundErrorCodesCoverUnion =
  (typeof REFUND_ERROR_CODES)[number] extends RefundErrorCode ? true : never;
type _AssertRefundErrorCodesNoExtras =
  RefundErrorCode extends (typeof REFUND_ERROR_CODES)[number] ? true : never;

const _pinRefundErrorCodesCoverUnion: _AssertRefundErrorCodesCoverUnion = true;
const _pinRefundErrorCodesNoExtras: _AssertRefundErrorCodesNoExtras = true;
void _pinRefundErrorCodesCoverUnion;
void _pinRefundErrorCodesNoExtras;

// ---------------------------------------------------------------------------
// GET /api/orders/seller-orders/[id]/refund-state
// ---------------------------------------------------------------------------

/**
 * Refund-state snapshot the modal reads on open. Populates "Max refundable:
 * $X" (= `remaining.total`), the per-item budget inputs, and the shipping
 * toggle disabled-state (Q8 — driven by `shippingRefundable`). The `attempts`
 * array feeds the read-only history strip.
 *
 * `shippingMode` is the Shippo vs flat-rate discriminator on the seller_order.
 * `shippingRefundable` collapses the label-lifecycle state onto a single
 * boolean the UI branches on:
 *  - `flat`   → always `true`
 *  - `shippo` → `true` iff label state is NONE or PURCHASED-not-yet-scanned;
 *               flips `false` at first tracking scan (state B onwards).
 *
 * @see .tondocs/payments/REFUND_V2.md §5.3
 * @see .tondocs/payments/REFUND_V2.md §10 Q8
 */
export interface RefundStateResponse {
  sellerOrderId: string;
  currency: string;
  shippingMode: "shippo" | "flat";
  shippingRefundable: boolean;
  captured: {
    subtotal: number;
    shipping: number;
    serviceFee: number;
    total: number;
  };
  refunded: {
    /** Per-order-item cumulative completed refund (buyer-facing item amount). */
    items: Record<string, number>;
    shipping: number;
    serviceFee: number;
    platformFee: number;
    total: number;
  };
  remaining: {
    /** Per-order-item remaining refundable = original − Σ completed. */
    items: Record<string, number>;
    shipping: number;
    total: number;
  };
  attempts: Array<{
    id: string;
    status: "pending" | "completed" | "failed" | "reversed" | "cancelled";
    source: "cancel" | "goodwill" | "dispute";
    totalAmount: number;
    /** ISO-8601 UTC. */
    createdAt: string;
  }>;
}

const MoneyBucketSchema = z.object({
  subtotal: z.number(),
  shipping: z.number(),
  serviceFee: z.number(),
  total: z.number(),
});

const RefundedBucketSchema = z.object({
  items: z.record(z.string(), z.number()),
  shipping: z.number(),
  serviceFee: z.number(),
  platformFee: z.number(),
  total: z.number(),
});

const RemainingBucketSchema = z.object({
  items: z.record(z.string(), z.number()),
  shipping: z.number(),
  total: z.number(),
});

const RefundAttemptSummarySchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "completed", "failed", "reversed", "cancelled"]),
  source: z.enum(["cancel", "goodwill", "dispute"]),
  totalAmount: z.number(),
  createdAt: z.string(),
});

export const RefundStateResponseSchema = pinned<RefundStateResponse>()(
  z.object({
    sellerOrderId: z.string().uuid(),
    currency: z.string().min(3),
    shippingMode: z.enum(["shippo", "flat"]),
    shippingRefundable: z.boolean(),
    captured: MoneyBucketSchema,
    refunded: RefundedBucketSchema,
    remaining: RemainingBucketSchema,
    attempts: z.array(RefundAttemptSummarySchema),
  }),
);
